/**
 * DYNAMIC FOOTER
 * Adapts branding, links, and theme based on current site
 * OPTIMIZED: Using smaller logo image with explicit dimensions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

// Optimized logo - Using the AVIF doctor image (47KB vs 902KB PNG)
const OPTIMIZED_LOGO = "/hero/doctor-avatar-160.avif";

export const DynamicFooter = () => {
  const { 
    title, 
    domain, 
    theme, 
    conditions, 
    doctor, 
    bodyPart,
    isMainSite,
    getRelatedSites,
    allSites,
    id: currentSiteId
  } = useSite();

  // Safe fallbacks
  const safeTheme = theme || { primaryHex: '#0e7490', lightBgHex: '#f0f9ff' };
  const safeDoctor = doctor || { name: 'Dr. DN Reddy', phone: '+91 99599 64567', email: 'info@drdnreddyortho.com', address: 'Apollo Hospitals, Financial District, Hyderabad' };
  const safeBodyPart = bodyPart || 'Orthopedic';
  const safeDomain = domain || 'drdnreddyortho.com';
  const safeConditions = conditions || [];

  const relatedSites = getRelatedSites ? getRelatedSites() : [];
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand & Contact */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src={OPTIMIZED_LOGO}
                alt={`${safeDoctor.name} - Upper Limb Surgeon`}
                width="56"
                height="56"
                className="h-14 w-14 rounded-full object-cover"
                loading="lazy"
              />
              {isMainSite && isMainSite() ? (
                <span className="text-xl font-bold" style={{ color: safeTheme.primaryHex }}>{safeDoctor.name}</span>
              ) : (
                <div className="text-xl font-bold">
                  <span style={{ color: safeTheme.primaryHex }}>{safeDomain.split('.')[0]}</span>
                  <span className="text-gray-300">.{safeDomain.split('.').slice(1).join('.')}</span>
                </div>
              )}
            </div>
            <p className="text-gray-300 text-sm mb-4">
              Expert {safeBodyPart} care by {safeDoctor.name}
            </p>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" style={{ color: safeTheme.primaryHex }} />
                <span>{safeDoctor.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" style={{ color: safeTheme.primaryHex }} />
                <span>{safeDoctor.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" style={{ color: safeTheme.primaryHex }} />
                <span>{safeDoctor.address}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Doctor</Link></li>
              <li><Link to="/team" className="hover:text-white transition-colors">Our Team</Link></li>
              <li><Link to="/testimonials" className="hover:text-white transition-colors">Testimonials</Link></li>
              <li><Link to="/faq" className="hover:text-white transition-colors">FAQs</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Health Blog</Link></li>
              <li><Link to="/publications" className="hover:text-white transition-colors">Publications</Link></li>
              <li><Link to="/in-the-news" className="hover:text-white transition-colors">In The News</Link></li>
              <li><Link to="/media-kit" className="hover:text-white transition-colors" data-testid="footer-media-kit">Media Kit</Link></li>
              <li><Link to="/resources" className="hover:text-white transition-colors">Patient Resources</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
              <li><Link to="/international-patients" className="hover:text-white transition-colors">International Patients</Link></li>
              <li><Link to="/fellowships" className="hover:text-white transition-colors">Fellowships</Link></li>
            </ul>
          </div>

          {/* Conditions */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Conditions We Treat</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/frozen-shoulder" className="hover:text-white transition-colors">Frozen Shoulder</Link></li>
              <li><Link to="/rotator-cuff-tear" className="hover:text-white transition-colors">Rotator Cuff Tear</Link></li>
              <li><Link to="/shoulder-dislocation" className="hover:text-white transition-colors">Shoulder Dislocation</Link></li>
              <li><Link to="/shoulder-arthritis" className="hover:text-white transition-colors">Shoulder Arthritis</Link></li>
              <li><Link to="/tennis-elbow" className="hover:text-white transition-colors">Tennis Elbow</Link></li>
              <li><Link to="/golfers-elbow" className="hover:text-white transition-colors">Golfers Elbow</Link></li>
              <li><Link to="/carpal-tunnel" className="hover:text-white transition-colors">Carpal Tunnel</Link></li>
              <li><Link to="/trigger-finger" className="hover:text-white transition-colors">Trigger Finger</Link></li>
              <li><Link to="/knee-arthritis" className="hover:text-white transition-colors">Knee Arthritis</Link></li>
              <li><Link to="/hip-arthritis" className="hover:text-white transition-colors">Hip Arthritis</Link></li>
              <li><Link to="/acl-reconstruction" className="hover:text-white transition-colors">ACL Reconstruction</Link></li>
              <li><Link to="/meniscal-tears" className="hover:text-white transition-colors">Meniscal Tears</Link></li>
              <li><Link to="/sports-injuries" className="hover:text-white transition-colors">Sports Injuries</Link></li>
              <li><Link to="/avascular-necrosis" className="hover:text-white transition-colors">Avascular Necrosis</Link></li>
              <li><Link to="/ankle-injuries" className="hover:text-white transition-colors">Ankle Injuries</Link></li>
              <li><Link to="/trauma" className="hover:text-white transition-colors">Trauma Care</Link></li>
            </ul>
          </div>

          {/* Surgeries & Services */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Surgeries & Services</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/knee-replacement" className="hover:text-white transition-colors">Knee Replacement</Link></li>
              <li><Link to="/hip-replacement" className="hover:text-white transition-colors">Hip Replacement</Link></li>
              <li><Link to="/shoulder-replacement" className="hover:text-white transition-colors">Shoulder Replacement</Link></li>
              <li><Link to="/joint-replacement" className="hover:text-white transition-colors">Joint Replacement</Link></li>
              <li><Link to="/shoulder-arthroscopy" className="hover:text-white transition-colors">Shoulder Arthroscopy</Link></li>
              <li><Link to="/knee-arthroscopy" className="hover:text-white transition-colors">Knee Arthroscopy</Link></li>
              <li><Link to="/robotic-surgery" className="hover:text-white transition-colors">Robotic Surgery</Link></li>
              <li><Link to="/daa-hip-replacement" className="hover:text-white transition-colors">DAA Hip Replacement</Link></li>
              <li><Link to="/precision-surgery" className="hover:text-white transition-colors">Precision Surgery</Link></li>
              <li><Link to="/cartilage-repair" className="hover:text-white transition-colors">Cartilage Repair</Link></li>
            </ul>
            
            <h3 className="font-semibold mb-3 mt-6 text-white">Recovery & Resources</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/knee-replacement-recovery-timeline" className="hover:text-white transition-colors">Knee Recovery Timeline</Link></li>
              <li><Link to="/hip-replacement-recovery" className="hover:text-white transition-colors">Hip Recovery Guide</Link></li>
              <li><Link to="/exercises-for-frozen-shoulder" className="hover:text-white transition-colors">Frozen Shoulder Exercises</Link></li>
              <li><Link to="/physiotherapy-after-knee-surgery" className="hover:text-white transition-colors">Post-Surgery Physio</Link></li>
              <li><Link to="/shoulder-pain-at-night" className="hover:text-white transition-colors">Shoulder Pain at Night</Link></li>
            </ul>
          </div>

          {/* Cost & Location */}
          <div>
            <h3 className="font-semibold mb-4 text-white">Cost Information</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/joint-replacement-cost-hyderabad" className="hover:text-white transition-colors">Joint Replacement Cost</Link></li>
              <li><Link to="/knee-replacement-cost-hyderabad" className="hover:text-white transition-colors">Knee Replacement Cost</Link></li>
              <li><Link to="/hip-replacement-cost-hyderabad" className="hover:text-white transition-colors">Hip Replacement Cost</Link></li>
              <li><Link to="/rotator-cuff-surgery-cost-india" className="hover:text-white transition-colors">Rotator Cuff Surgery Cost</Link></li>
              <li><Link to="/surgery-cost-india" className="hover:text-white transition-colors">Surgery Cost India</Link></li>
              <li><Link to="/medical-tourism-joint-surgery-india" className="hover:text-white transition-colors">Medical Tourism India</Link></li>
            </ul>
            
            <h3 className="font-semibold mb-3 mt-6 text-white">Find Us</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li><Link to="/orthopedic-surgeon-hyderabad" className="hover:text-white transition-colors">Orthopedic Surgeon Hyderabad</Link></li>
              <li><Link to="/best-ortho-doctor-hyderabad" className="hover:text-white transition-colors">Best Ortho Doctor Hyderabad</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Cross-site navigation bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <p className="text-center text-sm font-medium text-gray-400 mb-4">
            Dr. Deepthi Nandan Reddy's Specialist Network
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm" data-testid="network-sites">
            {allSites && Object.values(allSites).map((site) => (
              <a
                key={site.id}
                href={`https://${site.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`${site.title} - ${site.domain}`}
                data-testid={`network-link-${site.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  site.id === currentSiteId 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: site.theme?.primaryHex || '#0e7490' }}
                ></span>
                <span>
                  <span className="font-medium">{site.title}</span>
                  <span className="text-gray-400 ml-1.5">{site.domain}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Dr. Reddy online — external profiles & specialty sites (followed links for entity/SEO) */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6" data-testid="footer-online-profiles">
          <p className="text-center text-sm font-medium text-gray-400 mb-4">Dr. Deepthi Nandan Reddy online</p>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-300">
            <a href="https://www.apollohospitals.com/doctors/orthopedician/hyderabad/dr-deepthi-nandan-reddy" target="_blank" rel="noopener" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-profile-apollo"><ExternalLink className="w-3.5 h-3.5" /> Apollo Hospitals Profile</a>
            <a href="https://www.apollo247.com" target="_blank" rel="noopener" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-profile-apollo247"><ExternalLink className="w-3.5 h-3.5" /> Apollo 24|7</a>
            <a href="https://www.practo.com/hyderabad/doctor/dr-a-deepthi-nandan-reddy-orthopedist" target="_blank" rel="noopener" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-profile-practo"><ExternalLink className="w-3.5 h-3.5" /> Practo</a>
            <a href="https://www.justdial.com/Hyderabad/Dr-Deepthi-Nandan-Reddy-Hi-Tech-City-Gachibowli/040PXX40-XX40-180117094742-R6J3_BZDET" target="_blank" rel="noopener" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-profile-justdial"><ExternalLink className="w-3.5 h-3.5" /> Justdial</a>
            <a href="https://www.instagram.com/drdnortho/" target="_blank" rel="noopener" className="hover:text-white transition-colors inline-flex items-center gap-1.5" data-testid="footer-profile-instagram"><ExternalLink className="w-3.5 h-3.5" /> Instagram</a>
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-gray-400 mt-3">
            <span className="text-gray-500">Specialty sites:</span>
            <a href="https://shoulder.co.in" target="_blank" rel="noopener" className="hover:text-white transition-colors" data-testid="footer-specialty-shoulder">Shoulder (shoulder.co.in)</a>
            <a href="https://elbow.co.in" target="_blank" rel="noopener" className="hover:text-white transition-colors" data-testid="footer-specialty-elbow">Elbow (elbow.co.in)</a>
            <a href="https://handandwrist.co.in" target="_blank" rel="noopener" className="hover:text-white transition-colors" data-testid="footer-specialty-handwrist">Hand &amp; Wrist (handandwrist.co.in)</a>
          </div>
        </div>
      </div>


      {/* Copyright */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© {currentYear} {safeDoctor.name}. All rights reserved.</p>
            <div className="flex gap-4 mt-2 md:mt-0">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Medical Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
